/**
 * Figma-driven row model for the Audited Financials redesign (RECUR-27010).
 *
 * Row order + backend `classificationName` mapping (`src`) ported from the approved mock;
 * PROFIT_AND_LOSS_LAYOUT repointed to the RECUR-27014 statutory waterfall.
 *
 * DISPLAY LABELS are aligned VERBATIM to the comprehensive-details source sheet per UAT
 * RECUR-26713 (e.g. "Reserves and Surplus", "Non-current" lowercase-c on the BS, "Income Tax",
 * "Cash Flows from / (Used in) … Activities", "…Before Effect of Exchange Rate Changes").
 * Only `label` (what renders) changed for that alignment — every `src` (the backend join key)
 * is unchanged, so the data joins are untouched.
 *
 * This is the single source of truth for which rows render, in what order, under what label,
 * and how each joins to the backend (by `classificationName` via `src`). It does NOT depend on
 * backend row order — the backend is joined into this fixed shape. Every row is purely
 * presentational and renders fully expanded — there is no collapse model.
 *
 * `src: null` rows are structural headers/labels with no backend value — rendered as bands/blank.
 * `Total Liabilities` and `Total Non Current Assets` backend subtotals are intentionally omitted.
 */

export type AuditedRowType = 'group' | 'sub' | 'item' | 'subtotal';

export interface AuditedRowSpec {
    type: AuditedRowType;
    /** Display label — always what's rendered (aligned to the source sheet, UAT RECUR-26713). */
    label: string;
    /** Backend `classificationName` this row's values are joined from, or null if none exists. */
    src: string | null;
    /** True when `label` differs from the backend's own display name for `src`. */
    rename?: boolean;
    /** True for statement-level grand totals (e.g. Total Equity and Liabilities / Total Assets),
     *  which render a level up from section subtotals (less indented). */
    grand?: boolean;
    /** Hide the row entirely when the payload has no row for `src` (RECUR-28287: OCI/TCI only
     *  exist for docs with OCI). Without this flag a missing `src` renders '—', as today. */
    hideWhenAbsent?: boolean;
}

export const BALANCE_SHEET_LAYOUT: AuditedRowSpec[] = [
    { type: 'group', label: 'Equity and Liabilities', src: null },
    { type: 'sub', label: 'Equity', src: null },
    { type: 'item', label: 'Share Capital', src: 'Share Capital' },
    { type: 'item', label: 'Reserves and Surplus', src: 'Reserves and Surplus' },
    { type: 'item', label: 'Other Equity', src: 'Other Equity' },
    { type: 'subtotal', label: 'Total Equity', src: 'Total Shareholder Funds', rename: true },
    { type: 'sub', label: 'Liabilities', src: null },
    { type: 'sub', label: 'Non-current Liabilities', src: null },
    { type: 'item', label: 'Long Term Borrowings', src: 'Long Term Borrowings' },
    { type: 'item', label: 'Net Deferred Tax Liabilities', src: 'Net Deferred Tax Liabilities' },
    { type: 'item', label: 'Other Long Term Liabilities', src: 'Non Current Liabilities', rename: true },
    { type: 'item', label: 'Long Term Provisions', src: 'Long Term Provisions' },
    {
        type: 'subtotal',
        label: 'Total Non-current Liabilities',
        src: 'Total Non Current Liabilities',
        rename: true,
    },
    { type: 'sub', label: 'Current Liabilities', src: null },
    { type: 'item', label: 'Short Term Borrowings', src: 'Short Term Borrowings' },
    { type: 'item', label: 'Trade Payables', src: 'Trade Payables' },
    { type: 'item', label: 'Other Current Liabilities', src: 'Other Liabilities', rename: true },
    { type: 'item', label: 'Short Term Provisions', src: 'Short Term Provisions' },
    { type: 'subtotal', label: 'Total Current Liabilities', src: 'Total Current Liabilities' },
    { type: 'subtotal', label: 'Total Equity and Liabilities', src: 'Total Equities Liabilities', rename: true, grand: true },
    { type: 'group', label: 'Assets', src: null },
    { type: 'sub', label: 'Net Fixed Assets', src: null },
    { type: 'item', label: 'Tangible Assets', src: 'Tangible Assets' },
    { type: 'item', label: 'Intangible Assets', src: 'Intangible Assets' },
    { type: 'subtotal', label: 'Total Net Fixed Assets', src: 'Net Fixed Assets', rename: true },
    // RECUR-26712: Capital WIP is a standalone line between Net Fixed Assets and Other
    // Non-current Assets in the Excel — not part of either subtotal, but counted in Total
    // Assets (served via the CAPITAL_WORK_IN_PROGRESS switch case in getBalanceSheetAggregatedData).
    { type: 'item', label: 'Capital Work-in-progress', src: 'Capital Work-in-Progress', rename: true },
    { type: 'sub', label: 'Other Non-current Assets', src: null },
    { type: 'item', label: 'Non-current Investments', src: 'Investments', rename: true },
    { type: 'item', label: 'Net Deferred Tax Assets', src: 'Net Deferred Tax Assets' },
    { type: 'item', label: 'Long Term Loans and Advances', src: 'Long Term Loans and Advances' },
    { type: 'item', label: 'Other Non-current Assets', src: 'Non Current Assets', rename: true },
    {
        type: 'subtotal',
        label: 'Total Other Non-current Assets',
        src: 'Total Other Non-Current Assets',
        rename: true,
    },
    { type: 'sub', label: 'Current Assets', src: null },
    { type: 'item', label: 'Current Investments', src: 'Current Investments' },
    { type: 'item', label: 'Inventories', src: 'Inventories' },
    { type: 'item', label: 'Trade Receivables', src: 'Trade Receivables' },
    { type: 'item', label: 'Cash and Bank Balances', src: 'Cash And Cash Equivalents', rename: true },
    { type: 'item', label: 'Short Term Loans and Advances', src: 'Other Advances / Deposits', rename: true },
    { type: 'item', label: 'Other Current Assets', src: 'Other Assets', rename: true },
    { type: 'subtotal', label: 'Total Current Assets', src: 'Total Current Assets' },
    { type: 'subtotal', label: 'Total Assets', src: 'Total Assets', grand: true },
];

export const PROFIT_AND_LOSS_LAYOUT: AuditedRowSpec[] = [
    // UAT (Nishank, Aug 2026): Net Revenue renders with the subtotal treatment (bold, grey band)
    // like Total Operating Cost — `type` is presentational only, the data join is unchanged.
    { type: 'subtotal', label: 'Net Revenue', src: 'Revenue', rename: true },
    { type: 'group', label: 'Operating Cost', src: null },
    { type: 'item', label: 'Cost of Materials Consumed', src: 'Cost of Materials Consumed' },
    { type: 'item', label: 'Purchases of Stock-in-trade', src: 'Purchases of Stock in Trade', rename: true },
    {
        type: 'item',
        label: 'Changes in Inventories / Finished Goods',
        src: 'Changes in Inventories / Finished Goods',
    },
    { type: 'item', label: 'Employee Benefit Expense', src: 'Employee Benefit Expense' },
    { type: 'item', label: 'Other Expenses', src: 'Other Expenses' },
    { type: 'subtotal', label: 'Total Operating Cost', src: 'Total Operating Cost' },
    { type: 'subtotal', label: 'Operating Profit (EBITDA)', src: 'Operating Profit (EBITDA)' },
    { type: 'item', label: 'Other Income', src: 'Other Income' },
    { type: 'item', label: 'Depreciation and Amortization Expense', src: 'Depreciation', rename: true },
    { type: 'subtotal', label: 'Profit Before Interest and Tax', src: 'Profit Before Interest and Tax' },
    { type: 'item', label: 'Finance Costs', src: 'Finance Costs' },
    {
        type: 'subtotal',
        label: 'Profit Before Tax and Exceptional Items Before Tax',
        src: 'Profit Before Tax and Exceptional Items',
        rename: true,
    },
    { type: 'item', label: 'Exceptional Items Before Tax', src: 'Exceptional Items Before Tax' },
    { type: 'subtotal', label: 'Profit Before Tax', src: 'Profit Before Tax' },
    { type: 'item', label: 'Income Tax', src: 'Tax', rename: true },
    {
        type: 'subtotal',
        label: 'Profit for the Period from Continuing Operations',
        src: 'Profit for Period from Continuing Operations',
        rename: true,
    },
    {
        type: 'item',
        label: 'Profit from Discontinuing Operations After Tax',
        src: 'Profit from Discontinuing Operation After Tax',
        rename: true,
    },
    {
        type: 'item',
        label: 'Minority Interest and Profit from Associates and Joint Ventures',
        src: 'Minority Interest and Profit from Associates and Joint Ventures',
    },
    { type: 'subtotal', label: 'Profit for the Period', src: 'Profit for Period', rename: true },
    // RECUR-28287: Other Comprehensive Income (Ind-AS). Code-computed derived rows — hidden
    // entirely (not a '—' row) for any document/period with no OCI data.
    { type: 'subtotal', label: 'Other Comprehensive Income', src: 'Other Comprehensive Income', hideWhenAbsent: true },
    { type: 'subtotal', label: 'Total Comprehensive Income', src: 'Total Comprehensive Income', hideWhenAbsent: true },
];

// CF src values are the backend `classificationName`s produced by the RECUR-27009 serving code.
// Display labels aligned to the source sheet per UAT RECUR-26713 (full "Cash Flows from /
// (Used in) … Activities" wording; "Adjustments for …"; "…Before Effect of Exchange Rate Changes").
export const CASH_FLOW_LAYOUT: AuditedRowSpec[] = [
    { type: 'group', label: 'Cash Flows from / (Used in) Operating Activities', src: null },
    { type: 'item', label: 'Profit Before Tax', src: 'Profit Before Tax (Cash Flow)', rename: true },
    {
        type: 'item',
        label: 'Adjustment for Finance Cost and Depreciation',
        src: 'Adjustment for Finance Cost and Depreciation',
    },
    {
        type: 'item',
        label: 'Adjustments for Current and Non-Current Assets',
        src: 'Adjustment for Current and Non-Current Assets',
        rename: true,
    },
    {
        type: 'item',
        label: 'Adjustments for Current and Non-Current Liabilities',
        src: 'Adjustment for Current and Non-Current Liabilities',
        rename: true,
    },
    {
        type: 'item',
        label: 'Other Adjustments in Operating Activities',
        src: 'Other Adjustments in Operating Activities',
    },
    {
        type: 'subtotal',
        label: 'Net Cash Flows from / (Used in) Operating Activities',
        src: 'Net Cash Flow from Operating Activities',
        rename: true,
    },
    { type: 'group', label: 'Cash Flows from / (Used in) Investing Activities', src: null },
    { type: 'item', label: 'Cash Outflow from Purchase of Assets', src: 'Cash Outflow from Purchase of Assets' },
    { type: 'item', label: 'Cash Inflow from Sale of Assets', src: 'Cash Inflow from Sale of Assets' },
    { type: 'item', label: 'Income from Assets', src: 'Income from Assets' },
    {
        type: 'item',
        label: 'Other Adjustments in Investing Activities',
        src: 'Other Adjustments in Investing Activities',
    },
    {
        type: 'subtotal',
        label: 'Net Cash Flows from / (Used in) Investing Activities',
        src: 'Net Cash Flow from Investing Activities',
        rename: true,
    },
    { type: 'group', label: 'Cash Flows from / (Used in) Financing Activities', src: null },
    {
        type: 'item',
        label: 'Cash Outflow from Repayment of Capital and Borrowings',
        src: 'Cash Outflow from Repayment of Capital and Borrowings',
    },
    {
        type: 'item',
        label: 'Cash Inflow from Raising Capital and Borrowings',
        src: 'Cash Inflow from Raising Capital and Borrowings',
    },
    { type: 'item', label: 'Interest and Dividends Paid', src: 'Interest and Dividends Paid' },
    {
        type: 'item',
        label: 'Other Adjustments in Financing Activities',
        src: 'Other Adjustments in Financing Activities',
    },
    {
        type: 'subtotal',
        label: 'Net Cash Flows from / (Used in) Financing Activities',
        src: 'Net Cash Flow from Financing Activities',
        rename: true,
    },
    {
        type: 'subtotal',
        label: 'Increase / (Decrease) in Cash and Cash Equivalents Before Effect of Exchange Rate Changes',
        src: 'Increase/(Decrease) in Cash',
        rename: true,
    },
    { type: 'subtotal', label: 'Adjustments to Cash and Cash Equivalents', src: 'Adjustments to Cash and Cash Equivalents' },
    { type: 'subtotal', label: 'Net Increase / (Decrease) in Cash and Cash Equivalents', src: 'Net Increase/(Decrease)', rename: true },
    { type: 'subtotal', label: 'Cash and Cash Equivalents at End of Period', src: 'Cash and Cash Equivalents at End of Period' },
];

// ── Units selector (RECUR-27010) ────────────────────────────────────────────
// The audited statements display every figure in a chosen unit. Default is Lakh.
// Rescaling is applied at render time in AuditedStatementTable (the backend still
// returns raw ₹ values); the divisor maps the unit label to its scale factor.
export const AUDITED_UNIT_INR = 'INR'; // default — raw ₹ values, shown exactly as the backend formats them
export const AUDITED_UNIT_LAKHS = 'Lakhs';
export const AUDITED_UNIT_CRORE = 'Crore';
export const AUDITED_UNIT_DEFAULT = AUDITED_UNIT_INR;
export const AUDITED_UNIT_OPTIONS = [AUDITED_UNIT_INR, AUDITED_UNIT_LAKHS, AUDITED_UNIT_CRORE];
// localStorage key: persist the viewer's unit choice across refresh / re-entry.
export const AUDITED_UNIT_STORAGE_KEY = 'aica.auditedFinancials.unit';
/** Provisional's key lives here beside the audited one so both are visible in one place
 *  (RECUR-27640). Separate keys are deliberate — see useUnitPreference. */
export const PROVISIONAL_UNIT_STORAGE_KEY = 'aica.provisionalFinancials.unit';
// Short label shown in grey after each statement header, e.g. "Balance Sheet (All values in Cr)".
export const AUDITED_UNIT_SHORT_LABEL: Record<string, string> = {
    [AUDITED_UNIT_INR]: '₹',
    [AUDITED_UNIT_LAKHS]: 'Lakhs',
    [AUDITED_UNIT_CRORE]: 'Cr',
};
// Divisor of 1 (INR) means "no rescale" — the raw value passes through untouched.
export const AUDITED_UNIT_DIVISOR: Record<string, number> = {
    [AUDITED_UNIT_INR]: 1,
    [AUDITED_UNIT_LAKHS]: 1e5,
    [AUDITED_UNIT_CRORE]: 1e7,
};

// First-column header label: "Classification" on Audited (default), "Financial Year" on
// Provisional (see each component's `firstColumnLabel` prop). DISPLAY-only — never use these for
// the `header.name !== 'Classification'` backend-key sentinel that AuditedStatementTable/-Section
// (and useAuditedStatementData.tsx / EditableTable.tsx / RawTable.tsx / ClassifiedTable.tsx)
// filter on elsewhere; that sentinel stays a raw literal, unrelated to the display label.
export const CLASSIFICATION_COLUMN_LABEL = 'Classification';
export const FINANCIAL_YEAR_COLUMN_LABEL = 'Financial Year';
